/* @flow */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

// Hooks
import useSelf from 'src/shared/domain/user/hook/useSelf';
import useSocialSharing from 'plugin/social-sharing/hook/useSocialSharing';
import useLocations from 'src/shared/domain/location/hook/useLocations';

// Utils
import messages from 'src/shared/app/braindate/l10n/braindateL10n';
import userMessages from 'src/shared/domain/user/l10n/userL10n';
import modalRegistry from 'src/shared/app/base/modal/registry/modalRegistry';
import { getUserId } from '@braindate/domain/lib/user/util';
import { generatePath } from 'src/shared/app/base/route/util/routeUtils';
import { topicEditRoute } from 'src/shared/app/base/route/setting/routeSettings';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import { userRecommendTopic } from 'src/shared/domain/user/action/userActions';
import { getTopicConversation } from 'src/shared/domain/conversation/selector/conversationSelectors';
import { socialSharingModalId } from 'src/shared/app/social/constant/socialSharingConstant';
import { participantsListModalId } from 'src/shared/app/braindate/constant/braindateConstants';
import { getBraindateConversation } from '@braindate/domain/lib/braindate/util';
import {
  isTopicLive,
  isTopicPast,
} from 'src/shared/domain/topic/util/topicUtil';
import {
  triggerModal,
  clearModal,
} from 'src/shared/app/base/modal/slice/modalSlice';
import {
  getConversationPath,
  getConversationParticipants,
} from '@braindate/domain/lib/conversation/util';
import {
  getTopicId,
  isGroupTopic,
  isFishbowlTopic,
  getGroupTopic,
  getOneOnOneTopic,
  getTopicURL,
  getTopicAuthor,
  getTopicBasedOnInterest,
} from '@braindate/domain/lib/topic/util';
import { isLocationsOver } from '@braindate/domain/lib/location/util';

// Components
import OptionsMenu from 'src/shared/ui/component/menu/OptionsMenu';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
// import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SocialSharingModal from 'src/shared/app/social/component/SocialSharingModal';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import RecommendationModal from 'src/shared/components/domain/user/form/RecommendationModal';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import BraindateParticipants from '../../braindate/component/participantList/BraindateParticipants';
import { getBraindateTopic } from 'src/shared/domain/braindate/util/braindateUtil';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';

// Type
import type { Topic } from '@braindate/domain/lib/topic/type';
import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { Conversation } from '@braindate/domain/lib/conversation/type';
import type { MenuPopoverItemProps } from 'src/shared/ui/component/menu/MenuPopoverItem';

modalRegistry.add(socialSharingModalId, SocialSharingModal);
modalRegistry.add(participantsListModalId, BraindateParticipants);

type Props = {
  topic?: Topic,
  braindate?: Braindate,
  showEdit?: boolean,
  classes?: {
    root?: string,
    button?: string,
  },
};

const TopicMenu = ({
  topic: topicProps,
  braindate,
  showEdit = true,
  classes = {},
}: Props) => {
  const intl = useIntl();
  const self = useSelf();
  const dispatch = useDispatch();

  const isSocialSharingEnabled = useSocialSharing();
  const { isRecommendedUserEnabled } = useFeatureDecisions();

  if (!topicProps && !braindate) {
    throw new Error('Either braindate or topic must be defined');
  }

  const topic = braindate ? getBraindateTopic(braindate) : topicProps;

  if (!topic) {
    throw new Error('Topic was undefined');
  }

  /*
  |------------------------------------------------------------------------------
  | Hooks
  |------------------------------------------------------------------------------
  */

  /*
  |------------------------------------------------------------------------------
  | Data
  |------------------------------------------------------------------------------
  */

  const topicId = getTopicId(topic);
  const isGroupBraindate = isGroupTopic(topic);
  const isFishbowl = isFishbowlTopic(topic);
  const isPast = !!isGroupTopic(topic) && isTopicPast(getGroupTopic(topic));
  const isLive = isTopicLive(topic);
  const isHost = self && getUserId(getTopicAuthor(topic)) === getUserId(self);
  const isConnectOnInterest =
    !isGroupBraindate &&
    !!getTopicBasedOnInterest(getOneOnOneTopic(topic), intl.locale);

  const conversation: ?Conversation = useSelector((state) =>
    braindate
      ? getBraindateConversation(braindate)
      : getTopicConversation(state, { id: topicId }),
  );
  const locations = useLocations();
  const isEventOver = isLocationsOver(locations);

  const [showRecommendModal, setShowRecommendModal] =
    React.useState<boolean>(false);

  const conversationParticipants = conversation
    ? getConversationParticipants(conversation)
    : [];
  const userJoinedConversation: boolean =
    !!self &&
    conversationParticipants.some(
      (participant) => getUserId(self) === getUserId(participant),
    );

  /*
  |------------------------------------------------------------------------------
  | Actions
  |------------------------------------------------------------------------------
  */
  const showRecommedTopicModal = () => {
    setShowRecommendModal(true);
  };

  const handleSubmitRecommendation = async (values) => {
    const { user: receiverUser, message } = values;

    await dispatch(
      userRecommendTopic(getUserId(receiverUser), getTopicURL(topic), message),
    );
    dispatch(clearModal());
  };

  const showParticipantsListModal = () => {
    dispatch(
      triggerModal(participantsListModalId, {
        topicId,
      }),
    );
  };

  const showSocialSharingModal = () => {
    dispatch(
      triggerModal(socialSharingModalId, {
        topicId,
      }),
    );
  };

  /*
  |------------------------------------------------------------------------------
  | Elements
  |------------------------------------------------------------------------------
  */

  const menuItems: MenuPopoverItemProps[] = [
    ...(isGroupBraindate
      ? [
          {
            title: intl.formatMessage(messages.participantList),
            icon: <PeopleOutlinedIcon />,
            onClick: showParticipantsListModal,
            // @TODO - Readd a prefvetch but check if we need to prefetch before because we are currently spamming the API
            // onMouseEnter: prefetchUsers,
          },
        ]
      : []),
    ...(isHost && !isPast && showEdit
      ? [
          {
            title: intl.formatMessage(messages.editTopic),
            icon: <ModeEditOutlineOutlinedIcon />,
            to: generatePath(topicEditRoute, { id: topicId }),
          },
        ]
      : []),
    // That option is commented following https://e180.atlassian.net/browse/PAX-3667
    // ...(!isPast && !isGroupBraindate && conversation && userJoinedConversation
    //   ? [
    //       {
    //         title: intl.formatMessage(messages.cancelBraindate),
    //         icon: <CloseOutlinedIcon />,
    //         to: getConversationPath(conversation),
    //       },
    //     ]
    //   : []),
    ...(conversation && userJoinedConversation && !isFishbowl
      ? [
          {
            title: intl.formatMessage(
              isPast ? messages.viewMessages : messages.sendMessage,
            ),
            icon: <ChatBubbleOutlineOutlinedIcon />,
            to: getConversationPath(conversation),
          },
        ]
      : []),
    ...(!!isRecommendedUserEnabled &&
    !isConnectOnInterest &&
    !isEventOver &&
    (isGroupBraindate ? !isPast && !isLive : true)
      ? [
          {
            title: intl.formatMessage(messages.recommend),
            icon: <EmailOutlinedIcon />,
            onClick: showRecommedTopicModal,
          },
        ]
      : []),
    ...(!!isSocialSharingEnabled && !isPast && !isConnectOnInterest
      ? [
          {
            title: intl.formatMessage(messages.share),
            icon: <IosShareOutlinedIcon />,
            onClick: showSocialSharingModal,
          },
        ]
      : []),
  ];

  return (
    <>
      <OptionsMenu options={menuItems} classes={classes} />

      {showRecommendModal ? (
        <RecommendationModal
          title={intl.formatMessage(userMessages.recommendTopicTitle)}
          onSubmit={handleSubmitRecommendation}
          onClose={() => setShowRecommendModal(false)}
        />
      ) : null}
    </>
  );
};

export default React.memo<Props>(TopicMenu);
